import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './SelectionContextToolbarComponent.css';
import { useSelectionStore } from '../../store/selectionStore';
import { usePropertiesPanelStore } from '../../store/propertiesPanelStore';
import { useDocumentsStateStore } from '../../store/documentsStateStore';
import { getSceneObjectSummary, SceneObjectSummary } from '../../utils/sceneObjectSummary';
import { CopyIcon, InfoIcon, MoveIcon, TrashIcon } from '../Icons';
import { emitter } from '../../input/AppEvents';
import CommandIdEnum from '../../enums/CommandIdEnums';
import hasRequiredRights from '../../commands/commandUserRights';

// Contextual toolbar shown just above the floorplan/3D toggle buttons whenever SelectCmd has an
// active selection: a colored type swatch + short label/value preview, then icon actions.
const SelectionContextToolbarComponent = () => {
    const { t } = useTranslation();
    const selectedSceneObjectUuids = useSelectionStore(state => state.selectedSceneObjectUuids);
    const openPropertiesPanel = usePropertiesPanelStore(state => state.open);

    const [summary, setSummary] = useState<SceneObjectSummary | null>(null);
    const [canDelete, setCanDelete] = useState<boolean>(false);
    const [canMove, setCanMove] = useState<boolean>(false);
    const [canClone, setCanClone] = useState<boolean>(false);
    const [isCloneable, setIsCloneable] = useState<boolean>(false);

    useEffect(() => {
        if (selectedSceneObjectUuids.length === 0) {
            setSummary(null);
            setCanDelete(false);
            setCanMove(false);
            setCanClone(false);
            setIsCloneable(false);
            return;
        }

        const activeDocument = useDocumentsStateStore.getState().getActiveDocument();
        // Types that opt out of the toolbar (e.g. checklist items) stay selectable, the toolbar
        // just isn't shown for them.
        const sceneObject = selectedSceneObjectUuids
            .map(uuid => activeDocument?.document.getSceneObject(uuid))
            .find(candidate => candidate?.hasSelectionContextToolbar() ?? false);
        setSummary(sceneObject ? getSceneObjectSummary(sceneObject) : null);
        setCanDelete(activeDocument ? hasRequiredRights(activeDocument.document.userRight, CommandIdEnum.DeleteSceneObject) : false);
        setCanMove(activeDocument ? hasRequiredRights(activeDocument.document.userRight, CommandIdEnum.MoveSceneObjects) : false);

        // Not every type can be duplicated, so the whole selection is consulted: the action stays
        // offered for a mixed selection (the command skips what it can't copy) and is left out
        // entirely when there is nothing in it worth cloning. Rights, as elsewhere here, only
        // disable it.
        setIsCloneable(selectedSceneObjectUuids
            .some(uuid => activeDocument?.document.getSceneObject(uuid)?.canClone() ?? false));
        setCanClone(activeDocument ? hasRequiredRights(activeDocument.document.userRight, CommandIdEnum.CloneSceneObjects) : false);
    }, [selectedSceneObjectUuids]);

    // The whole selection is passed on: MoveSceneObjectsCmd moves every object it's given by the
    // same delta, so it already covers multi-select even though selection is single today.
    const handleMove = () => {
        emitter.emit('commandLaunch', {
            commandId: CommandIdEnum.MoveSceneObjects,
            parameters: selectedSceneObjectUuids,
        });
    };

    // Like Move, the whole selection is passed on: CloneSceneObjectsCmd copies every cloneable
    // object it's given and skips the rest.
    const handleClone = () => {
        emitter.emit('commandLaunch', {
            commandId: CommandIdEnum.CloneSceneObjects,
            parameters: selectedSceneObjectUuids,
        });
    };

    const handleDelete = () => {
        emitter.emit('commandLaunch', {
            commandId: CommandIdEnum.DeleteSceneObject,
            parameters: selectedSceneObjectUuids[0],
        });
    };

    if (!summary) {
        return null;
    }

    return (
        <div className="selection-context-toolbar">
            <div className="selection-context-toolbar-id">
                <span className="selection-context-toolbar-swatch" style={{ backgroundColor: summary.color }} />

                <div className="selection-context-toolbar-text">
                    <div className="selection-context-toolbar-label">{t(summary.labelKey)}</div>
                    {summary.value && <div className="selection-context-toolbar-value">{summary.value}</div>}
                </div>
            </div>

            <div className="selection-context-toolbar-divider" />

            <button
                className="selection-context-toolbar-button"
                title={t('ui.selectionToolbar.info')}
                aria-label={t('ui.selectionToolbar.info')}
                onClick={openPropertiesPanel}
            >
                <InfoIcon />
            </button>

            <button
                className="selection-context-toolbar-button"
                title={t('ui.selectionToolbar.move')}
                aria-label={t('ui.selectionToolbar.move')}
                disabled={!canMove}
                onClick={handleMove}
            >
                <MoveIcon />
            </button>

            {isCloneable && (
                <button
                    className="selection-context-toolbar-button"
                    title={t('ui.selectionToolbar.clone')}
                    aria-label={t('ui.selectionToolbar.clone')}
                    disabled={!canClone}
                    onClick={handleClone}
                >
                    <CopyIcon />
                </button>
            )}

            <div className="selection-context-toolbar-divider" />

            <button
                className="selection-context-toolbar-button selection-context-toolbar-button-delete"
                title={t('ui.selectionToolbar.delete')}
                aria-label={t('ui.selectionToolbar.delete')}
                disabled={!canDelete}
                onClick={handleDelete}
            >
                <TrashIcon />
            </button>
        </div>
    );
};

export default SelectionContextToolbarComponent;
